import type { CollabListRow } from "@creators/backend/lib/collabs/queries";
import { isTerminal, statusPill } from "@creators/backend/lib/collabs/state-machine";

/**
 * Filtering by status, shared by the collab rail and Work history.
 *
 * Fine-grained ids still match a row's pill label (so "Delivered" covers both
 * draft_submitted and published). Work history only surfaces a short bucket
 * list — stage detail stays on each row.
 */
export type StatusFilter =
  | "all"
  | "active"
  | "pending"
  | "negotiating"
  | "awaiting-payment"
  | "in-progress"
  | "delivered"
  | "post-live"
  | "completed"
  | "cancelled"
  | "expired"
  | "closed";

/** Inbox dropdown — slightly tighter than every pill, still stage-aware. */
export const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "pending", label: "Pending" },
  { id: "awaiting-payment", label: "Awaiting payment" },
  { id: "in-progress", label: "In progress" },
  { id: "completed", label: "Completed" },
  { id: "closed", label: "Closed" },
];

/**
 * Work history tabs — four buckets. Mid-lifecycle stages live under Active;
 * cancelled + expired share Closed.
 */
export const WORK_HISTORY_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "completed", label: "Completed" },
  { id: "closed", label: "Closed" },
];

const KNOWN_FILTERS = new Set<string>([
  ...STATUS_FILTERS.map((filter) => filter.id),
  ...WORK_HISTORY_FILTERS.map((filter) => filter.id),
  "negotiating",
  "delivered",
  "post-live",
  "cancelled",
  "expired",
]);

/** The one fine-grained filter a collab belongs to, from its pill label. */
export function statusFilterOf(collab: CollabListRow): StatusFilter {
  const { label } = statusPill(collab.displayStatus, collab.paymentDue);
  return label.toLowerCase().replace(/\s+/g, "-") as StatusFilter;
}

/** Quiet Basic offers — hidden from the default Active inbox until they move on. */
export function isQuietBasicOffer(collab: CollabListRow): boolean {
  return collab.boostTier === "basic" && collab.status === "offer_pending";
}

export function collabMatchesStatus(collab: CollabListRow, filter: StatusFilter): boolean {
  if (filter === "all") return true;
  if (filter === "active") {
    // Default inbox: paid / in-flight work only — not unboosted opening offers.
    if (isQuietBasicOffer(collab)) return false;
    return !isTerminal(collab.displayStatus);
  }
  if (filter === "closed") {
    const stage = statusFilterOf(collab);
    return stage === "cancelled" || stage === "expired";
  }
  return statusFilterOf(collab) === filter;
}

/** Coerce legacy / deep links into a Work history tab. */
export function toWorkHistoryFilter(filter: StatusFilter): StatusFilter {
  if (filter === "completed" || filter === "closed" || filter === "all" || filter === "active") {
    return filter;
  }
  if (filter === "cancelled" || filter === "expired") return "closed";
  // pending, negotiating, awaiting-payment, in-progress, delivered, post-live
  return "active";
}

export function countByStatus(
  collabs: CollabListRow[],
  filters: { id: StatusFilter; label: string }[] = STATUS_FILTERS,
): Record<StatusFilter, number> {
  return filters.reduce(
    (counts, filter) => {
      counts[filter.id] = collabs.filter((collab) => collabMatchesStatus(collab, filter.id)).length;
      return counts;
    },
    {} as Record<StatusFilter, number>,
  );
}

export function parseStatusFilter(value: string | undefined): StatusFilter {
  return value && KNOWN_FILTERS.has(value) ? (value as StatusFilter) : "all";
}
